import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Outlet, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MainNavbar from '../components/Navbar/MainNavbar';
import Typography from '../components/Typography';
import EventManagementHeader from '../components/EventManagementHeader';
import axiosClient from '../api/axiosClient';
import { EventProvider } from '../context/EventContext';
import PublishEventModal from '../components/Modal/PublishEventModal';
import ErrorPage from '../components/ErrorPage';
import { theme } from '../theme';

const EventManagement = () => {
  const { id } = useParams();
  const location = useLocation();
  const { t } = useTranslation('manageEvent');
  const [eventDetails, setEventDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorType, setErrorType] = useState('generic');
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [eventPublished, setEventPublished] = useState(false);

  // Verificar si debemos mostrar el modal de publicación
  useEffect(() => {
    if (location.state?.showPublishModal) {
      setShowPublishModal(true);
      // Limpiar el state para evitar que se muestre el modal en futuras navegaciones
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        // Usar el nuevo endpoint protegido para gestión de eventos
        const response = await axiosClient.get(`/api/events/manage/${id}`);
        if (response.data.success) {
          setEventDetails(response.data.event);
        } else {
          setError(response.data.message || 'Error al obtener el evento.');
        }
      } catch (err) {
        console.error('Error fetching event details:', err);
        if (err.response?.status === 403) {
          setErrorType('unauthorized');
        } else if (err.response?.status === 404) {
          setErrorType('notFound');
        } else if (!err.response) {
          setErrorType('networkError');
        } else if (err.response?.status >= 500) {
          setErrorType('serverError');
        } else {
          setErrorType('generic');
        }
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
  }, [id]);

  const handleRetry = () => {
    setError(null);
    setLoading(true);
    setErrorType('generic');

    const fetchEvent = async () => {
      try {
        // Usar el nuevo endpoint protegido para gestión de eventos
        const response = await axiosClient.get(`/api/events/manage/${id}`);
        if (response.data.success) {
          setEventDetails(response.data.event);
        } else {
          setError(response.data.message || 'Error al obtener el evento.');
        }
      } catch (err) {
        console.error('Error fetching event details:', err);
        if (err.response?.status === 403) {
          setErrorType('unauthorized');
        } else if (err.response?.status === 404) {
          setErrorType('notFound');
        } else if (!err.response) {
          setErrorType('networkError');
        } else if (err.response?.status >= 500) {
          setErrorType('serverError');
        } else {
          setErrorType('generic');
        }
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
  };

  const handleClosePublishModal = () => {
    setShowPublishModal(false);
  };

  const handleEventPublish = (publishedEvent) => {
    setEventDetails(publishedEvent);
    setEventPublished(true);
  };

  if (loading) {
    return (
      <LoadingMessage>
        <Typography $variant="body-2-medium" color={theme.colors.defaultWeak}>
          {t('loading')}
        </Typography>
      </LoadingMessage>

    );
  }

  if (error) {
    return (
      <ErrorPage
        errorType={errorType}
        onRetry={handleRetry}
        showRetry={true}
        showGoBack={true}
        showGoHome={true}
      />
    );
  }

  if (!eventDetails) {
    return (
      <ErrorPage
        errorType="notFound"
        onRetry={handleRetry}
        showRetry={true}
        showGoBack={true}
        showGoHome={true}
      />
    );
  }

  return (
    <>
      <MainNavbar />
      <EventProvider
        initialEventDetails={eventDetails}
        eventPublished={eventPublished}
      >
        <EventManagementHeader />
        <Outlet />
      </EventProvider>
      <PublishEventModal
        isOpen={showPublishModal}
        onClose={handleClosePublishModal}
        eventId={id}
        onPublish={handleEventPublish}
      />
    </>
  );
};

export default EventManagement;

const LoadingMessage = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
`;